import * as pdfjsLib from 'pdfjs-dist';

/**
 * Custom PDF data-range transport for pdf.js. Fetches only the byte ranges
 * pdf.js asks for — instead of handing pdf.js the URL and letting it open one
 * long-lived request that keeps transferring the whole file even after pdf.js
 * stops reading.
 *
 * pdf.js has no per-range error channel: a range that never arrives leaves the
 * page waiting forever. So failures are retried with backoff (a background
 * warm-cache download fills the HTTP cache, so a later retry is served
 * locally), and a 200 — a server ignoring `Range` — is sliced to the requested
 * window instead of being fed whole at `begin`, which would corrupt the doc.
 *
 * Copied from the source page's private implementation so the ground truth
 * page streams identically; consolidating the source page onto this export is
 * a later cleanup.
 */
export class BlobRangeTransport extends (pdfjsLib as any).PDFDataRangeTransport {
  private static readonly RETRY_DELAYS_MS = [250, 500, 1000];
  private localBlob: Blob | null;

  constructor(length: number, private readonly rangeUrl: string, localBlob: Blob | null = null) {
    super(length, null, false);
    this.localBlob = localBlob;
  }

  /** Once the whole file is stored locally, serve every further range from it. */
  useLocalBlob(blob: Blob): void {
    this.localBlob = blob;
  }

  requestDataRange(begin: number, end: number): void {
    if (this.localBlob) {
      void this.readLocalRange(begin, end);
      return;
    }
    void this.fetchRange(begin, end, 0);
  }

  /** blob.slice() reads only the requested window from disk — memory stays lazy. */
  private async readLocalRange(begin: number, end: number): Promise<void> {
    try {
      const buf = await this.localBlob!.slice(begin, end).arrayBuffer();
      (this as any).onDataRange(begin, new Uint8Array(buf));
    } catch {
      this.localBlob = null;
      void this.fetchRange(begin, end, 0);
    }
  }

  private async fetchRange(begin: number, end: number, attempt: number): Promise<void> {
    try {
      const response = await fetch(this.rangeUrl, { headers: { Range: `bytes=${begin}-${end - 1}` } });
      if (response.status !== 206 && response.status !== 200) {
        throw new Error(`HTTP ${response.status}`);
      }
      let bytes = new Uint8Array(await response.arrayBuffer());
      if (response.status === 200 && bytes.length > end - begin) {
        bytes = bytes.slice(begin, end);
      }
      (this as any).onDataRange(begin, bytes);
    } catch {
      const delay = BlobRangeTransport.RETRY_DELAYS_MS[attempt];
      if (delay !== undefined) {
        setTimeout(() => void this.fetchRange(begin, end, attempt + 1), delay);
        return;
      }
    }
  }

  abort(): void { /* no-op */ }
}
